import { LoadingButton } from '@mui/lab';
import { Button, Typography } from '@mui/material';
import { FC, useCallback } from 'react';

import { BoxRow } from 'src/components/BoxRow';
import { Dialog } from 'src/components/Dialog';
import { Spacer } from 'src/components/Spacer';
import { useAlert } from 'src/services/alert/useAlert';
import { isEmpty, maybeCall } from 'src/utils/common';
import { useDeleteUsersMutation } from '../api';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  userIds: number[];
}

export const DeleteUsersModal: FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  userIds,
}) => {
  const { onOpenAlertDelayed } = useAlert();

  const [deleteUsers, { isLoading }] = useDeleteUsersMutation();

  const onSubmit = useCallback(async () => {
    try {
      await deleteUsers({
        userIds,
      }).unwrap();

      maybeCall(onSuccess);

      onOpenAlertDelayed({
        message: 'Selected users was successfully deleted.',
        title: 'Users deleted!',
        type: 'success',
      });
    } catch (err: any) {
      let message = 'There was an error, please try again.';
      if (err?.data?.message === 'AT_LEAST_ONE_SUPER_ADMIN_REQUIRED') {
        message = 'At least one Super Admin must remain in the system.';
      } else if (err?.data?.message === 'NO_SUITABLE_USERS_TO_DELETE') {
        message = 'None of the selected users can be deleted.';
      }
      onOpenAlertDelayed({ message, title: 'Oops!', type: 'error' });
    } finally {
      onClose();
    }
  }, [deleteUsers, onSuccess, userIds]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      sx={{
        alignItems: 'center',
        height: '320px',
        justifyContent: 'center',
        padding: '0 100px',
      }}
    >
      <Typography
        component="h2"
        fontSize="24px"
        fontWeight="600"
        sx={{
          color: '#D7335C',
          textAlign: 'center',
          margin: '0 0 20px',
        }}
      >
        Are you sure you want to delete selected users?
      </Typography>
      <Typography
        color="#356482"
        component="p"
        fontSize="16px"
        fontWeight="500"
        sx={{ mb: '32px', textAlign: 'center' }}
      >
        This action cannot be undone
      </Typography>
      <BoxRow sx={{ margin: 0, width: '100%' }}>
        <LoadingButton
          disabled={isEmpty(userIds) || isLoading}
          loading={isLoading}
          onClick={onSubmit}
          sx={{ flex: 1, height: '40px' }}
          variant="contained"
        >
          Delete
        </LoadingButton>
        <Spacer width="20px" />
        <Button
          onClick={onClose}
          sx={{ flex: 1, height: '40px' }}
          variant="outlined"
        >
          Cancel
        </Button>
      </BoxRow>
    </Dialog>
  );
};
